import React from "react";

import { coalesce } from "@kazoohr/helpers";

import { ControlWrapper, ControlWrapperProps } from "../ControlWrapper";
import { IconDictionary } from "../Icon";

import { RawUserSelect } from "./RawUserSelect";
import styles from "./UserSelect.scss";

/**
 * Shape of a user object
 */
export interface User {
  firstName?: string;
  id: string;
  image30?: string;
  image50?: string;
  image?: string;
  initials?: string;
  jobTitle?: string | { name?: string };
  lastName?: string;
  name: string;
  primaryGroup?: string | { name?: string };
  profileUrl?: string;
}

/**
 * Props for the UserSelect component
 */
export interface UserSelectProps extends Partial<ControlWrapperProps> {
  /**
   * The currently selected user
   */
  selectedUser?: User;
  /**
   * The currently selected users (for multi-select)
   */
  selectedUsers?: User[];
  /**
   * Whether to show the user icon
   */
  showIcon?: boolean;
  /**
   * Icon to be used for the input
   */
  icon?: IconDictionary;
  /**
   * Callback when the input text changes
   */
  onTextChange?: (text: string) => void;
  /**
   * Whether the input should take up full width
   */
  fullWidth?: boolean;
  /**
   * Callback when a user is selected
   */
  onUserSelected?: (user: User) => void;
  /**
   * Callback when the selected user is cleared
   */
  onUserCleared?: (user: User) => void;
  /**
   * Array of users to select from
   */
  users?: User[];
  /**
   * Maximum number of users that can be selected
   */
  maxUsers?: number;
  /**
   * Tooltip content
   */
  tooltip?: React.ReactNode;
  /**
   * Input ID for accessibility
   */
  id?: string;
  /**
   * Whether or not the menu should be rendered in a portal
   */
  renderInPortal?: boolean;
}

/**
 * UserSelect component that provides a searchable dropdown for selecting users.
 * Wraps RawUserSelect with a ControlWrapper for consistent form control styling and behavior.
 */
export function UserSelect({
  selectedUser,
  selectedUsers,
  showIcon,
  onTextChange,
  onUserSelected,
  onUserCleared,
  users = [],
  icon,
  maxUsers,
  id,
  tooltip,
  renderInPortal = true,
  ...controlProps
}: UserSelectProps) {
  const wrapperClass = maxUsers ? styles.wrapper : "";

  return (
    <ControlWrapper
      inputId={coalesce(id, "")}
      tooltip={tooltip}
      {...controlProps}
      wrapperClassNames={wrapperClass}
      renderControl={(inputProps) => {
        const { labelId, ...restInputProps } = inputProps;
        return (
          <RawUserSelect
            renderInPortal={renderInPortal}
            key={labelId}
            aria-autocomplete="list"
            selectedUser={selectedUser}
            selectedUsers={selectedUsers}
            showIcon={showIcon}
            icon={icon}
            onTextChange={onTextChange}
            onUserSelected={onUserSelected}
            onUserCleared={onUserCleared}
            users={users}
            maxUsers={maxUsers}
            multipleUsers={maxUsers === 0 || coalesce(maxUsers, 1) > 1}
            {...restInputProps}
          />
        );
      }}
    />
  );
}
